import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import OpeningBalanceNote from "./OpeningBalanceNote";
import { formatISTDate } from "../../lib/datetime";
import { plural } from "../../lib/vocabulary";

const ROLE_TAG = {
  sales_officer: "SO",
  assistant_manager: "Manager",
  admin_manager: "Manager",
};

/**
 * THE DIRECTOR'S PL OPENING-BALANCE ENTRY SURFACE (D-004, option 2).
 *
 * PL accrues only from the accrual epoch — days on duty before the app existed
 * were never observed and are not reconstructed. Pre-epoch service is honoured
 * by a figure the Director enters here, once per person, before go-live.
 *
 * WHY A SURFACE AND NOT A SCRIPT. The figures arrive separately and some of
 * them will be wrong; a script run once cannot be corrected by the person who
 * owns the numbers. So: a list of EVERY member of staff who files leave —
 * including, deliberately, the ones with nothing entered, because "who is
 * still missing a figure" is the question this page exists to answer and a
 * list of what has been entered cannot answer it.
 *
 * NO FIGURE IS INVENTED HERE. The input starts EMPTY, there is no default and
 * no placeholder number, and an entry without a figure or without a reason
 * cannot be submitted. The ceiling quoted comes from the response (Settings),
 * never from a literal — the same rule as every other threshold.
 *
 * A SECOND ENTRY IS AN AMENDMENT. The button says so, the dialog shows the
 * figure in force, and the server keeps both rows; nothing here overwrites a
 * history it cannot see.
 */
export default function OpeningBalancesPanel() {
  const [open, setOpen] = useState(false);
  const [surface, setSurface] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // the row being entered/amended
  const [figure, setFigure] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/leaves/opening-balances");
      setSurface(data);
    } catch (e) {
      // No fallback list. A surface that guesses who works here would be
      // guessing about payroll; it says it could not read instead.
      setSurface(null);
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !surface && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function startEntry(row) {
    setEditing(row);
    // EMPTY, always — including on an amendment. Pre-filling the figure in
    // force invites a Save that re-enters the number the Director came here to
    // change, and an audit row saying nothing happened is worse than none.
    setFigure("");
    setReason("");
  }

  async function submit(e) {
    e.preventDefault();
    if (figure === "" || Number.isNaN(Number(figure))) {
      toast.error("Enter the number of days");
      return;
    }
    if (!reason.trim()) {
      toast.error("Say where this figure comes from");
      return;
    }
    setSaving(true);
    try {
      await api.put("/leaves/opening-balance", {
        user_id: editing.user_id,
        opening: Number(figure),
        reason: reason.trim(),
      });
      toast.success(
        editing.opening_entered
          ? `Opening balance amended for ${editing.user_name}`
          : `Opening balance entered for ${editing.user_name}`
      );
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      className="bg-white border border-callisto rounded-xl"
      data-testid="opening-balances-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
        data-testid="opening-balances-toggle"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown size={16} className="text-callisto-navy/60" />
        ) : (
          <ChevronRight size={16} className="text-callisto-navy/60" />
        )}
        <Upload size={14} className="text-callisto-gold" />
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">
          PL opening balances
        </span>
        {/* A COUNT, NOT A STATUS FLAG: "everyone is done" and "there is nobody
            to ask about" both leave nothing pending, so the total is stated
            beside it and the summary is never a bare tick. */}
        {surface && (
          <span className="text-xs text-callisto-muted" data-testid="opening-balances-count">
            {surface.entered} of {plural(surface.total, "person", "people")} entered
            {surface.pending > 0 ? ` · ${surface.pending} still to enter` : ""}
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4">
          {surface && (
            <p className="mb-2 text-[11px] text-callisto-muted" data-testid="opening-balances-help">
              PL accrues from {formatISTDate(surface.epoch)}. Service before that
              date was never recorded by the app — enter it here, per person, as
              days carried in. Ceiling: {plural(surface.cap, "day")}. Every entry
              is logged with your name and reason, and shows on the employee's
              own balance page.
            </p>
          )}

          {error && (
            <div
              className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2"
              data-testid="opening-balances-error"
            >
              {error}
            </div>
          )}

          {loading && <Skeleton className="h-24 rounded-lg" data-testid="opening-balances-loading" />}

          {!loading && surface && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[560px]" data-testid="opening-balances-table">
                <thead>
                  <tr className="text-callisto-muted border-b border-callisto">
                    <th className="text-left font-semibold py-2">Employee</th>
                    <th className="text-right font-semibold py-2 px-2">Opening</th>
                    <th className="text-right font-semibold py-2 px-2">Accrued</th>
                    <th className="text-right font-semibold py-2 px-2">Balance</th>
                    <th className="text-left font-semibold py-2 pl-2">Entered by</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {surface.rows.map((row) => {
                    const latest = (row.opening_history || []).slice(-1)[0];
                    return (
                      <tr
                        key={row.user_id}
                        className="border-b border-callisto/50 last:border-0 align-top"
                        data-testid={`opening-balance-row-${row.user_id}`}
                      >
                        <td className="py-2 font-semibold text-callisto-navy">
                          {row.user_name}
                          <span className="ml-1 font-normal text-callisto-muted">
                            {ROLE_TAG[row.role] || row.role}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums">
                          {row.opening_entered ? (
                            <span className="font-bold text-callisto-navy">{row.opening}</span>
                          ) : (
                            // A gap is shown as a gap. "Not entered" is a
                            // different fact from a Director who looked and
                            // decided the answer was zero.
                            <span
                              className="text-orange-700 font-semibold"
                              data-testid={`opening-balance-pending-${row.user_id}`}
                            >
                              Not entered
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-callisto-muted">
                          {row.accrued}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums font-semibold text-callisto-navy">
                          {row.balance}
                        </td>
                        <td className="py-2 pl-2 text-callisto-muted">
                          {latest
                            ? `${latest.actor_name} on ${formatISTDate(latest.created_at)}`
                            : "—"}
                          {(row.opening_history || []).length > 1 && (
                            <span
                              className="ml-1 text-[10px] font-semibold text-callisto-navy"
                              data-testid={`opening-balance-amended-${row.user_id}`}
                            >
                              amended ×{row.opening_history.length - 1}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pl-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2.5 text-[11px]"
                            onClick={() => startEntry(row)}
                            data-testid={`opening-balance-enter-${row.user_id}`}
                          >
                            {row.opening_entered ? "Amend" : "Enter"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing?.opening_entered ? "Amend" : "Enter"} PL opening balance
            </DialogTitle>
            <DialogDescription>
              {editing?.user_name} — days of privilege leave carried in at the
              start of the scheme.
              {editing?.opening_entered && (
                <>
                  {" "}
                  The figure in force is {plural(editing.opening, "day")}; saving
                  a new one records an amendment and keeps the old entry.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {/* The employee's own view of what is on record, verbatim — so the
              Director is amending what the person can see, not a second
              rendering of it. */}
          {editing?.opening_entered && (
            <OpeningBalanceNote pl={editing} testId="opening-balance-dialog-current" />
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-callisto-navy">Opening balance (days)</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max={surface?.cap}
                value={figure}
                onChange={(e) => setFigure(e.target.value)}
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="opening-balance-figure"
              />
              <p className="mt-1 text-[11px] text-callisto-muted">
                Left blank on purpose — there is no default. The ceiling is{" "}
                {surface ? plural(surface.cap, "day") : "set in Settings"}.
              </p>
            </div>
            <div>
              <Label className="text-callisto-navy">Where this figure comes from</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="e.g. HR leave register to 31 Aug 2026"
                className="mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="opening-balance-reason"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-callisto-navy hover:bg-callisto-navy/90"
                data-testid="opening-balance-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save entry"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
